<script setup>
import { ref, computed, onMounted } from 'vue';
import { useRoute } from 'vue-router';
import { useAlert } from 'dashboard/composables';
/* global axios */

// 王吉涛自媒体线索列表(2026-09-18): 外部聊天软件经 wmt-lead-adapter 推送落库的数据。
// 仅 管理员+网络主管 可见(见 WmtLeadPolicy); 手机号本页明文。
// 后端 GET /api/v1/accounts/:id/wmt_leads?q=&page= (25/页, meta+payload 信封)

const route = useRoute();
const accountId = computed(() => route.params.accountId);

const loading = ref(false);
const loaded = ref(false);
const leads = ref([]);
const count = ref(0);
const page = ref(1);
const searchInput = ref('');
const activeQuery = ref('');
const detail = ref(null);
const detailLoading = ref(false);

const totalPages = computed(() => Math.max(1, Math.ceil(count.value / 25)));

const fetchList = async () => {
  loading.value = true;
  try {
    const res = await axios.get(
      `/api/v1/accounts/${accountId.value}/wmt_leads`,
      {
        params: {
          q: activeQuery.value || undefined,
          page: page.value,
        },
      }
    );
    leads.value = res.data.payload || [];
    count.value = res.data.meta?.count || 0;
    loaded.value = true;
  } catch (error) {
    const status = error?.response?.status;
    if (status === 403) {
      useAlert('无权访问自媒体线索', 'error');
    } else {
      useAlert('自媒体线索加载失败', 'error');
    }
  } finally {
    loading.value = false;
  }
};

const openDetail = async lead => {
  detailLoading.value = true;
  detail.value = { ...lead, raw_body: null };
  try {
    const res = await axios.get(
      `/api/v1/accounts/${accountId.value}/wmt_leads/${lead.id}`
    );
    detail.value = res.data.payload || detail.value;
  } catch {
    useAlert('详情加载失败', 'error');
  } finally {
    detailLoading.value = false;
  }
};

const doSearch = () => {
  activeQuery.value = searchInput.value.trim();
  page.value = 1;
  fetchList();
};

const goPage = p => {
  if (p < 1 || p > totalPages.value || p === page.value) return;
  page.value = p;
  fetchList();
};

const fmtTime = t => {
  if (!t) return '—';
  // 后端 UTC 时间串 → 北京时区显示
  const d = new Date(`${String(t).replace(' ', 'T')}Z`);
  if (Number.isNaN(d.getTime())) return t;
  return new Date(d.getTime() + 8 * 3600000)
    .toISOString()
    .slice(0, 16)
    .replace('T', ' ');
};

onMounted(fetchList);
</script>

<template>
  <div class="overflow-auto bg-n-surface-1 w-full px-6">
    <div class="max-w-5xl mx-auto py-6 space-y-4">
      <div class="flex items-end justify-between gap-4">
        <div>
          <h1 class="text-2xl font-semibold text-n-slate-12">自媒体线索</h1>
          <p class="text-sm text-n-slate-10 mt-0.5">
            王吉涛自媒体渠道推送的线索, 共 {{ count.toLocaleString() }} 条
          </p>
        </div>
        <div class="flex items-center gap-2 shrink-0">
          <input
            v-model="searchInput"
            type="search"
            placeholder="姓名/手机/账号/访客ID/QQ/渠道"
            class="w-56 px-3 py-1.5 text-sm rounded-lg bg-n-solid-3 border border-n-slate-5 text-n-slate-12 placeholder:text-n-slate-9 focus:outline-none focus:border-n-brand-8"
            @keyup.enter="doSearch"
          />
          <button
            class="px-3 py-1.5 text-sm rounded-lg bg-n-solid-3 border border-n-slate-5 text-n-slate-12 hover:bg-n-slate-4"
            @click="doSearch"
          >
            搜索
          </button>
          <button
            class="px-3 py-1.5 text-sm rounded-lg bg-n-solid-3 border border-n-slate-5 text-n-slate-12 hover:bg-n-slate-4"
            title="刷新"
            @click="fetchList"
          >
            ⟳
          </button>
        </div>
      </div>

      <div
        v-if="!loaded"
        class="bg-n-solid-3 rounded-xl py-24 text-center text-n-slate-10"
      >
        加载中...
      </div>

      <template v-else>
        <div
          class="bg-n-solid-3 rounded-xl overflow-hidden"
          :class="loading ? 'opacity-50' : ''"
        >
          <div
            v-if="!leads.length"
            class="py-16 text-center text-n-slate-10"
          >
            {{ activeQuery ? '没有匹配的线索' : '暂无线索数据' }}
          </div>
          <table v-else class="w-full text-sm">
            <thead>
              <tr class="bg-n-slate-2 border-b border-n-slate-4 text-n-slate-11">
                <th class="px-4 py-2.5 text-left font-medium whitespace-nowrap">入库时间</th>
                <th class="px-4 py-2.5 text-left font-medium whitespace-nowrap">姓名</th>
                <th class="px-4 py-2.5 text-left font-medium whitespace-nowrap">手机</th>
                <th class="px-4 py-2.5 text-left font-medium whitespace-nowrap">来源渠道</th>
                <th class="px-4 py-2.5 text-left font-medium whitespace-nowrap">账号名称</th>
                <th class="px-4 py-2.5 text-left font-medium whitespace-nowrap">来源省市</th>
                <th class="px-4 py-2.5 text-left font-medium whitespace-nowrap">QQ</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="it in leads"
                :key="it.id"
                class="border-b border-n-slate-3 hover:bg-n-brand/5 cursor-pointer"
                @click="openDetail(it)"
              >
                <td class="px-4 py-2 text-n-slate-11 whitespace-nowrap">
                  {{ fmtTime(it.created_at) }}
                </td>
                <td class="px-4 py-2 text-n-slate-12 font-medium whitespace-nowrap">
                  {{ it.linkman || '—' }}
                </td>
                <td class="px-4 py-2 text-n-slate-12 tabular-nums whitespace-nowrap">
                  {{ it.mobile || '—' }}
                </td>
                <td class="px-4 py-2 text-n-slate-11 whitespace-nowrap">
                  {{ it.channel_type || '—' }}
                </td>
                <td class="px-4 py-2 text-n-slate-11 whitespace-nowrap">
                  {{ it.account_name || '—' }}
                </td>
                <td class="px-4 py-2 text-n-slate-11 whitespace-nowrap">
                  {{ it.source_province || '—' }}
                </td>
                <td class="px-4 py-2 text-n-slate-11 whitespace-nowrap">
                  {{ it.qq || '—' }}
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <!-- 分页 -->
        <div
          v-if="totalPages > 1"
          class="flex items-center justify-between text-sm text-n-slate-11"
        >
          <span>第 {{ page }} / {{ totalPages }} 页</span>
          <div class="flex gap-2">
            <button
              class="px-3 py-1.5 rounded-lg bg-n-solid-3 border border-n-slate-5 hover:bg-n-slate-4 disabled:opacity-40"
              :disabled="page <= 1"
              @click="goPage(page - 1)"
            >
              上一页
            </button>
            <button
              class="px-3 py-1.5 rounded-lg bg-n-solid-3 border border-n-slate-5 hover:bg-n-slate-4 disabled:opacity-40"
              :disabled="page >= totalPages"
              @click="goPage(page + 1)"
            >
              下一页
            </button>
          </div>
        </div>
      </template>

      <!-- 详情弹层 -->
      <div
        v-if="detail"
        class="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40"
        @click.self="detail = null"
      >
        <div class="bg-n-surface-1 rounded-2xl w-full max-w-lg max-h-[85vh] overflow-auto shadow-xl">
          <div class="flex items-center justify-between px-5 py-4 border-b border-n-slate-4">
            <h2 class="text-lg font-semibold text-n-slate-12">
              线索详情 #{{ detail.id }}
            </h2>
            <button
              class="text-n-slate-10 hover:text-n-slate-12 text-xl leading-none"
              @click="detail = null"
            >
              ×
            </button>
          </div>
          <div class="px-5 py-4 space-y-2 text-sm">
            <div
              v-for="(v, k) in {
                姓名: detail.linkman,
                手机: detail.mobile,
                来源渠道: detail.channel_type,
                账号名称: detail.account_name,
                QQ: detail.qq,
                创建时间: detail.addtime,
                访客ID: detail.visitor_id,
                来源省市: detail.source_province,
                首次访问: detail.first_visit_time,
                推送格式: detail.push_format,
                重复推送: `${detail.push_count || 1} 次`,
                入库时间: fmtTime(detail.created_at),
              }"
              :key="k"
              class="flex gap-3 py-1.5 border-b border-n-slate-3"
            >
              <span class="w-24 shrink-0 text-n-slate-10">{{ k }}</span>
              <span class="text-n-slate-12 break-all">{{ v || '—' }}</span>
            </div>
            <div class="pt-3 flex items-center gap-3">
              <a
                v-if="detail.dialog_record_link"
                :href="detail.dialog_record_link"
                target="_blank"
                rel="noopener noreferrer"
                class="px-4 py-2 rounded-lg bg-n-brand-9 text-white text-sm font-medium hover:opacity-90"
              >
                打开聊天记录 ↗
              </a>
              <span
                v-if="detailLoading"
                class="text-xs text-n-slate-10"
              >加载中…</span>
            </div>
            <details
              v-if="detail.raw_body"
              class="pt-2"
            >
              <summary class="text-xs text-n-slate-10 cursor-pointer">
                原始推送报文
              </summary>
              <pre class="mt-2 p-3 rounded-lg bg-n-solid-3 text-xs text-n-slate-11 whitespace-pre-wrap break-all">{{ detail.raw_body }}</pre>
            </details>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
